/**
 * Cross-tab auth synchronization via BroadcastChannel.
 *
 * When tab A logs in/out, all other open tabs in the same origin react
 * within ~1 frame. The previous codebase had no multi-tab sync — tab B
 * would remain authenticated until its own /me call returned 401, which
 * could be tens of minutes after tab A's logout.
 *
 * Channel name: `glasspro:auth`. Single channel, two message types.
 *
 * Initialization is EAGER at module load (with SSR guard). A passive tab
 * that never broadcasts must still receive — lazy init would mean the
 * channel and its listener are created only on first send, leaving
 * passive tabs deaf to broadcasts from active tabs.
 *
 * FSD note: this module imports `useAuthStore` from `@/entities/auth/model`,
 * which is a layer violation in strict FSD (`shared` should not depend on
 * `entities`). The project already accepts this pattern in `src/shared/api/request.ts`
 * and `src/shared/api/auth.ts` for auth-related shared code; we follow the
 * established convention rather than introducing a third pattern.
 */

import { tokenMemory } from './memory'
import { localStore } from './localStorage'
import { queryClient } from '@/shared/api/queryClient'
import { useAuthStore } from '@/entities/auth/model'

// auth.ts imports from @/shared/storage (this module's parent barrel), so
// a static import here creates a circular. Lazy import inside the handler
// body breaks the cycle at runtime — the same pattern used in axios.ts.

type AuthMessage = { type: 'logout' } | { type: 'login'; userId: string | number }

const CHANNEL_NAME = 'glasspro:auth'

const channelSingleton: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined'
    ? null
    : (() => {
        const ch = new BroadcastChannel(CHANNEL_NAME)
        ch.onmessage = handleMessage
        return ch
      })()

function handleMessage(event: MessageEvent<AuthMessage>): void {
  const msg = event.data

  if (msg.type === 'logout') {
    // Mirror the broadcaster's local cleanup so this tab ends up in the
    // same coherent logged-out state. Cookies are already cross-tab —
    // the broadcaster's deleteAllCookies cleared them for us too.
    tokenMemory.clear()
    localStore.clearUserScoped()
    queryClient.clear()
    useAuthStore.getState().logout()

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login?reason=logged-out-elsewhere'
    }
    return
  }

  if (msg.type === 'login') {
    // User logged in elsewhere — possibly as a different account.
    // Call bootstrapAuth() to hydrate this tab's user state from /me.
    // Direct queryClient.invalidateQueries('/v1/me') is a no-op because
    // fetchMeAndHydrate uses raw axios, not a TanStack Query hook.
    import('@/shared/api/auth').then(({ bootstrapAuth }) => bootstrapAuth())
  }
}

export function broadcastLogout(): void {
  channelSingleton?.postMessage({ type: 'logout' } satisfies AuthMessage)
}

export function broadcastLogin(userId: string | number): void {
  channelSingleton?.postMessage({
    type: 'login',
    userId,
  } satisfies AuthMessage)
}
